import {
  allocateCouponDiscounts,
  getBulkDiscount,
  getCoverProcessingFeesSetting,
  getProcessingFeeContribution,
  resolveCouponReach,
  shareSubscriptionAmountOff,
} from '@repo/lib';
import {
  checkCanCoverProcessingFees,
  getIsCoverProcessingFeeTicked,
} from '../lib/checkCoverProcessingFee';
import useCheckoutState from './useCheckoutState';
import getFixedTaxRate from '../lib/getFixedTaxRate';
import getSetupFeeAmount from '../lib/getSetupFeeAmount';
import getTotalDueAmount from '../lib/getTotalDueAmount';
import getOneTimeTotalAmount from '../lib/getOneTimeTotalAmount';
import useTaxCalculation from './useTaxCalculation';
import getSubtotalAmount from '../lib/getSubtotalAmount';
import getRecurringTotalAmount from '../lib/getRecurringTotalAmount';
import useProductPrice from './useProductPrice';
import useSelectedPrice from './useSelectedPrice';
import useSetupFee from './useSetupFee';
import { isRecurringPurchase } from '../lib/multiPrice';
import useQuantity from './useQuantity';
import React from 'react';
import getSelectedVariants from '../lib/getSelectedVariants';
import getOneTimeVariantsAmount from '../lib/getOneTimeVariantsAmount';
import getSubscriptionVariantsAmount from '../lib/getSubscriptionVariantsAmount';
import getOnetimeSubtotalAmount from '../lib/getOneTimeSubtotalAmount';
import getSelectedTicketTypes from '../lib/getSelectedTicketTypes';
import getTicketTypeLineItemsWithTotals from '../lib/getTicketTypeLineItemsWithTotals';
import { PAGE_TYPES } from '../enums/page-types';
import { productTypes } from '../enums/product-types';
import getPlanIterations from '../lib/getPlanIterations';
import getFees from '../lib/getFees';
import { getProportionateDiscountAmounts } from '../lib/getProportionateDiscountAmount';
import getCouponDiscountAmount from '../lib/getCouponDiscountAmount';
import getTotalTicketQuantity from '../lib/getTotalTicketQuantity';
import { splitBulkDiscounts } from '../lib/splitBulkDiscounts';
import { getPartialAmount } from '../lib/getPartialAmount';
import getSelectedOrderBumps from '../lib/getSelectedOrderBumps';
import useProration from './useProration';
import { isProduction } from '../services/env';
import { checkIsTicketTypeCoupon } from '../lib/checkCoupon';
import getPriceBillingConfig from '../lib/getPriceBillingConfig';
import { toFixed } from 'accounting-js';

const CartCostContext = React.createContext();
const TicketCouponSharesContext = React.createContext(undefined);

// A subscription's one-time items carry only a fee's percentage part; a combined fee's fixed part
// rides the recurring fee line.
const getOneTimeItemFees = fees =>
  fees?.filter(fee => fee.percentage).map(fee => ({ ...fee, amount: null }));

export function checkIsTaxEnabled({ seller, page, product }) {
  if (!page?.tax?.enabled) return false;

  const taxMode = page.tax.mode || 'stripe';

  if (taxMode === 'fixed') {
    const fixedTaxRate = getFixedTaxRate({ page, product });
    return (
      fixedTaxRate != null &&
      typeof fixedTaxRate === 'object' &&
      typeof fixedTaxRate.percentage === 'number'
    );
  }

  if (taxMode === 'stripe') {
    return Boolean(seller?.tax?.enabled);
  }

  return false;
}

function getDefaultPrice(product) {
  const prices = product?.prices;
  if (!Array.isArray(prices) || prices.length === 0 || !product.defaultPriceId) return null;
  return prices.find(p => String(p._id) === String(product.defaultPriceId)) || null;
}

function getMode({ page, product, selectedPrice }) {
  if (page.type === PAGE_TYPES.CHECKOUT) {
    // Stripe Elements mode follows the buyer's selected price — confirming a
    // one-time PaymentIntent against Elements mounted in subscription mode
    // fails ("Payment failed"), which is exactly what happened on legacy
    // 'subscription'-typed products offering a one-time tier.
    if (selectedPrice) {
      return isRecurringPurchase(selectedPrice, product) ? 'subscription' : 'payment';
    }
    // No current selection — e.g. the buyer switched to a price-picker tab that
    // doesn't contain the merchant default, transiently clearing the choice.
    // Follow the DEFAULT price's mode rather than the legacy product.type: a
    // bare product.type fallback flips the mode (e.g. 'charge' -> 'payment') the
    // instant the selection clears, which changes the <Elements> key and
    // remounts the entire picker subtree — resetting its active tab so tabs
    // whose default lives elsewhere can never be opened (and discarding any card
    // details already entered). The mode still corrects itself the moment the
    // buyer picks a price.
    const defaultPrice = getDefaultPrice(product);
    if (defaultPrice) {
      return isRecurringPurchase(defaultPrice, product) ? 'subscription' : 'payment';
    }
    const firstEnabledPrice = (product?.prices || []).find(p => p.enabled !== false);
    if (firstEnabledPrice) {
      return firstEnabledPrice.billingType === 'recurring' ? 'subscription' : 'payment';
    }
    // TODO(M14): legacy product-scalar fallback (no prices[] at all) — remove once the prices[] backfill migration has run (product.type)
    if (product.type === productTypes.SUBSCRIPTION) {
      return 'subscription';
    } else if (product.type === productTypes.CHARGE) {
      return 'payment';
    }
  } else if (page.type === PAGE_TYPES.EVENT) {
    return 'payment';
  }

  return '';
}

function getCurrency(
  { page, product } = {
    page: {},
    product: {},
  },
) {
  if (page.type === PAGE_TYPES.CHECKOUT) {
    // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed (product.currency)
    return product.currency;
  } else if (page.type === PAGE_TYPES.EVENT) {
    return page.eventDetails.currency;
  }

  return '';
}

// Client twin of the server's apportionAmount (build-charge-tax-lines.ts):
// split `total` across `weights` pro-rata with largest-remainder rounding so
// the portions always sum exactly to `total`. All-zero weights split equally.
function apportionAmount(total, weights) {
  if (weights.length === 0) return [];

  const effectiveWeights = weights.some(weight => weight > 0) ? weights : weights.map(() => 1);
  const weightSum = effectiveWeights.reduce((sum, weight) => sum + weight, 0);

  const exactShares = effectiveWeights.map(weight => (total * weight) / weightSum);
  const portions = exactShares.map(share => Math.floor(share));
  let leftover = total - portions.reduce((sum, portion) => sum + portion, 0);

  const byRemainder = exactShares
    .map((share, index) => ({ index, remainder: share - Math.floor(share) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);

  for (const { index } of byRemainder) {
    if (leftover <= 0) break;
    portions[index] += 1;
    leftover -= 1;
  }

  return portions;
}

// Server-rounding fixed tax for one line (calculateFixedTax twin).
function calculateFixedTaxForAmount(amount, taxRate) {
  const percentage = taxRate?.percentage;

  if (!amount || !percentage) {
    return { amountExcludingTax: amount, taxAmount: 0, totalAmount: amount };
  }

  if (taxRate.inclusive) {
    const amountExcludingTax = Math.round(amount / (1 + percentage / 100));
    return { amountExcludingTax, taxAmount: amount - amountExcludingTax, totalAmount: amount };
  }

  const taxAmount = Math.round((amount * percentage) / 100);
  return { amountExcludingTax: amount, taxAmount, totalAmount: amount + taxAmount };
}

// The server's getOrderBumpUnitPrice, mirrored: on a PWYW bump the buyer's
// figure is the price and `offer.price.amount` is only the floor.
const orderBumpUnitPrice = ({ offer, payWhatYouWantAmount }) =>
  payWhatYouWantAmount != null ? payWhatYouWantAmount : offer.price.amount;

// The server's getOrderBumpLineAmount, mirrored: a bump carries its own setup
// fee on its own line, on the same terms as the primary's. Every site that
// totals, taxes or fees a bump goes through this — quoting the bare unit price
// would show the buyer less than the server charges.
const orderBumpSetupFeeAmount = selection =>
  getSetupFeeAmount(
    selection.offer.price.setupFee,
    selection.quantity,
    selection.offer.price.setupFeeMultipliesWithQuantity,
  );

const orderBumpLineAmount = selection =>
  orderBumpUnitPrice(selection) * selection.quantity + orderBumpSetupFeeAmount(selection);

// The server's getCouponBumpLines, mirrored: a bump's own setup fee joins the coupon's base only
// with appliesToSetupFee.
const getCouponBumpLine = (selection, coupon) => ({
  productId: selection.offer.productId,
  base:
    orderBumpLineAmount(selection) -
    (coupon?.appliesToSetupFee ? 0 : orderBumpSetupFeeAmount(selection)),
});

// Fixed-mode local mirror of the server's buildChargeTaxLines +
// calculateFixedTaxForLines for a one-time purchase with order bumps: the
// primary line plus one line per bump, every page fee folded across the lines
// pro-rata by discounted share. Every line is taxed at the PRIMARY's rate — a
// bump inherits the page product's treatment (2026-08-20 ruling) — so the
// split exists for the per-line amounts, not for mixed rates.
function calculateFixedTaxForOrderBumpLines({
  primaryAmount,
  primaryUndiscountedAmount,
  primaryTitle,
  primaryTaxRate,
  oneTimeSelectedOrderBumps,
  netOrderBumpLineAmount,
  fees,
}) {
  const lines = [
    {
      reference: 'primary',
      title: primaryTitle,
      amount: primaryAmount,
      undiscountedAmount: primaryUndiscountedAmount,
      taxRate: primaryTaxRate,
    },
    ...oneTimeSelectedOrderBumps.map(selection => ({
      reference: `bump:${selection.offer.productId}`,
      title: selection.offer.title,
      amount: netOrderBumpLineAmount(selection),
      undiscountedAmount: orderBumpLineAmount(selection),
      taxRate: primaryTaxRate,
    })),
  ];

  const discountedWeights = lines.map(line => line.amount);
  const weights = discountedWeights.some(weight => weight > 0)
    ? discountedWeights
    : lines.map(line => line.undiscountedAmount ?? line.amount);

  const amounts = lines.map(line => line.amount);
  for (const fee of fees) {
    const portions = apportionAmount(fee.amount, weights);
    portions.forEach((portion, index) => {
      amounts[index] += portion;
    });
  }

  let oneTimeTaxAmount = 0;
  let oneTimeTotalAmountExcludingTax = 0;
  let oneTimeTotalIncludingTax = 0;
  const taxBreakdownByLine = lines.map((line, index) => {
    const lineTax = calculateFixedTaxForAmount(amounts[index], line.taxRate);

    oneTimeTaxAmount += lineTax.taxAmount;
    oneTimeTotalAmountExcludingTax += lineTax.amountExcludingTax;
    oneTimeTotalIncludingTax += lineTax.totalAmount;

    return {
      reference: line.reference,
      title: line.title,
      taxAmount: lineTax.taxAmount,
      rateLabel: line.taxRate
        ? `${line.taxRate.displayName} (${line.taxRate.percentage}%)`
        : undefined,
    };
  });

  return {
    oneTimeTaxAmount,
    oneTimeTotalAmountExcludingTax,
    oneTimeTotalIncludingTax,
    taxBreakdownByLine,
  };
}

function CartCostProvider({ children }) {
  const {
    form,
    page,
    product,
    coupon,
    payWhatYouWantPrice,
    seller,
    dynamicPlanIterations,
    selectedPaymentMethod,
    paymentOption,
    effectiveSelectedPriceId,
  } = useCheckoutState();

  if (page.type === PAGE_TYPES.FORM) return children;

  const currency = getCurrency({ page, product });
  const taxEnabled = checkIsTaxEnabled({ seller, page, product });

  let {
    recurringTaxAmount,
    recurringAmountExcludingTax,
    recurringTaxAmounts,
    recurringTaxAmountBeforeProration,
    recurringAmountExcludingTaxBeforeProration,
    recurringTaxAmountsBeforeProration,
    oneTimeTaxAmount,
    oneTimeTotalAmountExcludingTax,
    oneTimeTotalIncludingTax,
    oneTimeTaxAmounts,
    totalTaxAmounts,
    totalTaxAmountsBeforeProration,
    taxBreakdownByLine,
    isCalculatingTax,
  } = useTaxCalculation(taxEnabled);

  const taxRate = totalTaxAmounts?.[0]?.percentage != null ? totalTaxAmounts[0].percentage : 0;

  const {
    proration,
    isRefetchingProration,
    isLoadingProration,
    hasProration: hasAnchor,
  } = useProration();
  // Inside an anchor's full-price window the first charge is the regular price.
  const hasProration = hasAnchor && !proration?.fullPrice;

  const productPriceAmount = useProductPrice();

  // Setup fee resolved from the selected price (multi-price) so the charge tracks
  // the buyer's selection; falls back to the product-level fee for legacy products.
  const { setupFee: resolvedSetupFee, setupFeeMultipliesWithQuantity: resolvedSetupFeeMultiplies } =
    useSetupFee();

  const quantity = useQuantity();

  const bulkDiscounts =
    page.type === PAGE_TYPES.EVENT ? page.eventDetails.discounts : product.discounts;

  // Split bulk discounts into three categories
  const { subtotalDiscounts, variantDiscounts, ticketTypeDiscounts } =
    splitBulkDiscounts(bulkDiscounts);

  const selectedVariants = getSelectedVariants(
    product.variants,
    form.variants,
    currency,
    effectiveSelectedPriceId,
  );

  const selectedTicketTypes = getSelectedTicketTypes(
    page.ticketGroups,
    form.ticketGroups,
    currency,
  );
  const totalTicketQuantity = getTotalTicketQuantity(selectedTicketTypes);

  const pageTicketTypeIds =
    page.ticketGroups?.flatMap(group => group.ticketTypes?.map(tt => tt._id) || []) || [];

  let oneTimeVariantsAmount = 0;
  let oneTimeVariantsBulkDiscountAmount = 0;
  let subscriptionVariantsAmount = 0;

  // The cost branch follows the buyer's selected price on multi-price
  // products; legacy products (no resolved selectable price) keep the
  // product-level type. Without this, a one-time price selected on a legacy
  // 'subscription'-typed product is costed (and mounted in Stripe Elements)
  // as a subscription and the payment fails.
  const selectedPrice = useSelectedPrice();
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type)
  const billingType = selectedPrice
    ? selectedPrice.billingType
    : product.type === 'subscription'
      ? 'recurring'
      : product.type === 'charge'
        ? 'one_time'
        : null;
  const isCharge = billingType === 'one_time';
  const isSubscription = billingType === 'recurring';
  // The amount helpers (getTotalDueAmount / getOneTimeTotalAmount / tax)
  // branch on the legacy type string — feed them the type derived from the
  // selected price so they agree with the isCharge/isSubscription branches.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type)
  const effectiveProductType =
    billingType === 'recurring'
      ? 'subscription'
      : billingType === 'one_time'
        ? 'charge'
        : product.type;
  // Trial / start date follow the selected price (per-price billing config on
  // multi-price products); legacy products keep the product-level values.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.trialPeriodDays/startDate)
  const selectedPriceConfig = getPriceBillingConfig(selectedPrice);
  const effectiveTrialPeriodDays = selectedPrice
    ? selectedPriceConfig?.trialPeriodDays
    : product?.trialPeriodDays;
  const effectiveStartDate = selectedPrice ? selectedPriceConfig?.startDate : product?.startDate;
  const isEvent = page.type === 'event';

  const primaryCouponLine = isEvent
    ? null
    : {
        productId: product._id,
        priceId: selectedPrice?._id ?? effectiveSelectedPriceId ?? null,
      };

  // Order bumps price standalone (spec D5): own price × own quantity, never
  // touched by the primary's bulk-discount tiers, and by a coupon only through
  // its own product and price. All the existing
  // intermediate variables below stay primary-only; bump money joins at the
  // exact sites the server adds it (after the coupon, before fees) and at the
  // exposed cartCost fields. They come in submission order, so the fixed-mode
  // fee apportionment's index tie-break below lands its cents on the same lines
  // as the server's.
  const selectedOrderBumps = getSelectedOrderBumps({
    page,
    product,
    selectedPrice,
    form,
    currency,
  });

  const couponBumpLines = selectedOrderBumps.map(selection => getCouponBumpLine(selection, coupon));
  const couponPurchase = {
    ticketTypeIds: (selectedTicketTypes ?? [])
      .filter(ticketType => Number(ticketType.quantity) > 0)
      .map(ticketType => ticketType._id),
    bumpProductIds: couponBumpLines.map(line => line.productId),
  };
  const couponReach = resolveCouponReach(coupon, {
    pageId: page._id,
    pageTicketTypeIds,
    priceId: primaryCouponLine?.priceId,
    ...couponPurchase,
  });

  const {
    totalTicketTypesAmount,
    totalTicketTypesBulkDiscountAmount,
    totalTicketTypesCouponDiscountAmount,
    totalTicketFeesAmount,
    totalTicketFeesSubtotalAmount,
    totalPaidTicketQuantity,
    lineItems: ticketTypeLineItems,
    ticketCouponLines,
    ticketCouponShares,
  } = getTicketTypeLineItemsWithTotals(
    selectedTicketTypes,
    ticketTypeDiscounts,
    coupon,
    taxRate,
    currency,
    page._id,
    pageTicketTypeIds,
    couponBumpLines,
    couponReach,
  );

  // The ticket-fee pool sits apart from the ticket prices (mirrors the API's
  // get-event-charge-data): bulk tiers key off the prices alone, and the
  // order coupon reaches the pool only through `appliesToTicketFees`. A
  // ticket-scoped coupon's share is already netted into each line's fee.
  const ticketFeesAmount = isEvent ? totalTicketFeesAmount : 0;
  const couponReachesTicketFees = Boolean(coupon?.appliesToTicketFees) && ticketFeesAmount > 0;
  // A fee pool inside the coupon's base is floored together with it, one
  // outside it joins after the floor (the get-charge-amount setup-fee rule).
  const addTicketFeesAfterCoupon = discountedAmount =>
    couponReachesTicketFees
      ? Math.max(0, discountedAmount + ticketFeesAmount)
      : Math.max(0, discountedAmount) + ticketFeesAmount;

  const getOrderBumpsTotal = selections =>
    selections.reduce((total, selection) => total + orderBumpLineAmount(selection), 0);

  const oneTimeSelectedOrderBumps = selectedOrderBumps.filter(
    ({ offer }) => offer.billingType !== 'recurring',
  );
  const oneTimeOrderBumpsAmount = getOrderBumpsTotal(oneTimeSelectedOrderBumps);
  const recurringOrderBumpsAmount = getOrderBumpsTotal(
    selectedOrderBumps.filter(({ offer }) => offer.billingType === 'recurring'),
  );
  const orderBumpsAmount = oneTimeOrderBumpsAmount + recurringOrderBumpsAmount;

  if (isCharge) {
    const oneTimeVariants = getOneTimeVariantsAmount(
      selectedVariants,
      quantity,
      'charge',
      variantDiscounts,
    );

    oneTimeVariantsAmount = oneTimeVariants.oneTimeVariantsAmount;
    oneTimeVariantsBulkDiscountAmount = oneTimeVariants.oneTimeVariantsDiscountAmount;
  } else if (isSubscription) {
    const oneTimeVariants = getOneTimeVariantsAmount(
      selectedVariants,
      quantity,
      'subscription',
      variantDiscounts,
    );

    oneTimeVariantsAmount = oneTimeVariants.oneTimeVariantsAmount;
    oneTimeVariantsBulkDiscountAmount = oneTimeVariants.oneTimeVariantsDiscountAmount;

    subscriptionVariantsAmount = getSubscriptionVariantsAmount(selectedVariants, quantity);
  }

  const prorationAmount = hasProration ? proration?.amount : 0;

  // A PWYW selected price bills the customer's typed amount each period, not the
  // price's stored minimum — substitute it into the recurring math so the subtotal
  // and "Total due" track the input. A blank/0 entry falls back to the minimum.
  // PWYW is per-price on multi-price products and product-level on single-price ones.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.payWhatYouWant)
  const isPayWhatYouWant = selectedPrice ? selectedPrice.payWhatYouWant : product.payWhatYouWant;
  const effectiveUnitPrice =
    (isPayWhatYouWant && Number(payWhatYouWantPrice)) || productPriceAmount;

  const singleIterationAmount = effectiveUnitPrice * quantity + subscriptionVariantsAmount;

  // Per-price for multi-price products; legacy single-price (no prices[]) keeps
  // product.planIterations — resolved like effectiveStartDate above so a payment
  // plan's iteration count (and the breakdown math) follows the selected price.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.planIterations)
  const effectivePlanIterations = selectedPrice
    ? selectedPriceConfig?.planIterations
    : product?.planIterations;
  const planIterations = getPlanIterations({
    productPlanIterations: effectivePlanIterations,
    allowDynamicPlanIterations: page.allowDynamicPlanIterations,
    dynamicPlanIterations,
  });

  // Calculate subtotal before applying any discounts
  const subtotalAmount = getSubtotalAmount({
    productPriceAmount: productPriceAmount,
    totalTicketTypesAmount: totalTicketTypesAmount,
    quantity,
    oneTimeVariantsAmount,
    subscriptionVariantsAmount,
    // Honour the typed PWYW amount (charge AND subscription branches of
    // getSubtotalAmount now both substitute it), else the subtotal sticks at the
    // selected price's minimum (productPriceAmount).
    payWhatYouWantPrice: isPayWhatYouWant && Number(payWhatYouWantPrice),
    productType: effectiveProductType,
    planIterations: planIterations,
  });

  // Apply ticket type & variant option bulk discounts to subtotal (excluding variants)
  // to calculate the subtotal bulk discount
  let subtotalAfterItemDiscounts;
  if (page.type === PAGE_TYPES.EVENT) {
    subtotalAfterItemDiscounts =
      subtotalAmount - totalTicketTypesBulkDiscountAmount - totalTicketTypesCouponDiscountAmount;
  } else {
    subtotalAfterItemDiscounts = subtotalAmount - oneTimeVariantsBulkDiscountAmount;
  }

  // Apply bulk discounts to subtotal (excluding variants)
  const { bulkDiscount: subtotalBulkDiscount, bulkDiscountAmount: subtotalBulkDiscountAmount } =
    getBulkDiscount(
      subtotalDiscounts,
      page.type === PAGE_TYPES.EVENT ? totalPaidTicketQuantity : quantity,
      subtotalAfterItemDiscounts,
    );

  // Setup fees multiply with quantity for subscriptions and one-time charges
  // alike (the API multiplies on both paths — keep the preview in lockstep).
  const setupFeeAmount = getSetupFeeAmount(resolvedSetupFee, quantity, resolvedSetupFeeMultiplies);

  let recurringSubtotalAmount = 0;
  if (isSubscription) {
    recurringSubtotalAmount = effectiveUnitPrice * quantity + subscriptionVariantsAmount;
  }

  const totalBulkDiscountAmount =
    subtotalBulkDiscountAmount +
    oneTimeVariantsBulkDiscountAmount +
    totalTicketTypesBulkDiscountAmount;

  const oneTimeSubtotalAmount = getOnetimeSubtotalAmount({
    productType: effectiveProductType,
    productPriceAmount,
    totalTicketTypesAmount,
    quantity,
    oneTimeVariantsAmount,
    payWhatYouWantPrice,
    bulkDiscountAmount: totalBulkDiscountAmount,
  });

  const getDiscountSplit = (recurringBase, oneTimeBase) =>
    getProportionateDiscountAmounts({
      recurringSubtotalAmount: recurringBase,
      oneTimeSubtotalAmount: oneTimeBase,
      setupFeeAmount,
      ticketFeesAmount,
      coupon,

      startDate: effectiveStartDate,
      trialPeriodDays: effectiveTrialPeriodDays,
      pageId: page._id,
      pageTicketTypes: pageTicketTypeIds,
      primary: primaryCouponLine,
      purchase: couponPurchase,
    });

  const couponAllocation = isSubscription
    ? allocateCouponDiscounts(coupon, {
        pageId: page._id,
        primary: { ...primaryCouponLine, base: 0 },
        bumps: couponBumpLines,
        amountOffStaysOnPrimary: true,
      })
    : allocateCouponDiscounts(coupon, {
        pageId: page._id,
        pageTicketTypeIds,
        primary: primaryCouponLine && {
          ...primaryCouponLine,
          base: Math.max(
            0,
            oneTimeSubtotalAmount + (coupon?.appliesToSetupFee ? setupFeeAmount : 0),
          ),
        },
        primaryBase: Math.max(
          0,
          oneTimeSubtotalAmount + (coupon?.appliesToTicketFees ? ticketFeesAmount : 0),
        ),
        ticketLines: ticketCouponLines,
        bumps: couponBumpLines,
      });
  // A subscription takes a coupon only through its main recurring price, bumps included.
  const couponReachesSubscription = couponAllocation.primaryScope !== 'none';

  // The server's getSubscriptionCouponDiscounts, mirrored: an amount off reaching a bump splits by
  // size across the plan, the one-time items and the bumps it reaches.
  const amountOffBumps = selectedOrderBumps.map((selection, index) => ({
    base: couponBumpLines[index].base,
    billingType: selection.offer.billingType === 'recurring' ? 'recurring' : 'one_time',
    reached: couponAllocation.bumpReached[index],
  }));
  // One that skips a recurring bump rides the main recurring item alone, capped at it.
  const isAmountOffOnMainItem =
    isSubscription &&
    Boolean(coupon?.amountOff) &&
    couponReachesSubscription &&
    amountOffBumps.some(bump => bump.billingType === 'recurring' && !bump.reached);
  const mainItemAmountOff = isAmountOffOnMainItem
    ? Math.min(coupon.amountOff, effectiveUnitPrice * quantity)
    : null;
  const isAmountOffSplitWithBumps =
    isSubscription &&
    !isAmountOffOnMainItem &&
    Boolean(coupon?.amountOff) &&
    couponReachesSubscription &&
    couponAllocation.bumpReached.some(Boolean);
  const reachedBumpsBase = billingType =>
    amountOffBumps.reduce(
      (sum, bump) => (bump.reached && bump.billingType === billingType ? sum + bump.base : sum),
      0,
    );
  const discountSplit = isAmountOffSplitWithBumps
    ? getDiscountSplit(
        recurringSubtotalAmount + reachedBumpsBase('recurring'),
        oneTimeSubtotalAmount + reachedBumpsBase('one_time'),
      )
    : getDiscountSplit(recurringSubtotalAmount, oneTimeSubtotalAmount);
  const amountOffShares = isAmountOffSplitWithBumps
    ? shareSubscriptionAmountOff({
        recurringShare: discountSplit.recurringDiscountAmount,
        oneTimeShare: discountSplit.oneTimeVariantsDiscountAmount,
        primaryRecurringBase: recurringSubtotalAmount,
        oneTimeVariantsBase: oneTimeSubtotalAmount,
        bumps: amountOffBumps,
      })
    : null;
  const recurringDiscountAmount =
    mainItemAmountOff ??
    amountOffShares?.primaryRecurringDiscount ??
    discountSplit.recurringDiscountAmount;
  const oneTimeVariantsDiscountAmount = isAmountOffOnMainItem
    ? 0
    : (amountOffShares?.oneTimeVariantsDiscount ?? discountSplit.oneTimeVariantsDiscountAmount);
  const setupFeeDiscountAmount = isAmountOffOnMainItem ? 0 : discountSplit.setupFeeDiscountAmount;

  const bumpCouponDiscounts =
    (isSubscription && !couponReachesSubscription) || isAmountOffOnMainItem
      ? couponBumpLines.map(() => 0)
      : (amountOffShares?.bumpDiscounts ?? couponAllocation.bumpDiscounts);
  // The bumps' shares are counted on their own lines.
  const orderCouponDiscountAmount =
    mainItemAmountOff ??
    discountSplit.couponDiscountAmount -
      (amountOffShares?.bumpDiscounts.reduce((total, amount) => total + amount, 0) ?? 0);
  const bumpCouponDiscountBySelection = new Map(
    selectedOrderBumps.map((selection, index) => [selection, bumpCouponDiscounts[index]]),
  );
  const bumpCouponDiscountAmount = bumpCouponDiscounts.reduce((total, amount) => total + amount, 0);
  const netOrderBumpLineAmount = selection =>
    orderBumpLineAmount(selection) - (bumpCouponDiscountBySelection.get(selection) ?? 0);
  const getNetOrderBumpsTotal = selections =>
    selections.reduce((total, selection) => total + netOrderBumpLineAmount(selection), 0);
  const oneTimeOrderBumpsNetAmount = getNetOrderBumpsTotal(oneTimeSelectedOrderBumps);
  const recurringOrderBumpsNetAmount = getNetOrderBumpsTotal(
    selectedOrderBumps.filter(({ offer }) => offer.billingType === 'recurring'),
  );
  const orderBumpsNetAmount = oneTimeOrderBumpsNetAmount + recurringOrderBumpsNetAmount;
  // An amount off is shared with the bumps it reaches, so the main line takes only its share.
  const isAmountOffSharedWithBumps =
    Boolean(coupon?.amountOff) &&
    couponAllocation.primaryScope === 'order' &&
    couponAllocation.bumpReached.some(Boolean);

  /**
   * A coupon can be applied to EITHER the order level of ticket type level.
   * We make sure to use the correct discount to avoid double discounting.
   */
  const couponDiscountAmount = (() => {
    let discount;
    if (checkIsTicketTypeCoupon(coupon, page._id, pageTicketTypeIds)) {
      discount = totalTicketTypesCouponDiscountAmount;
    } else if (isAmountOffSharedWithBumps && !isSubscription) {
      return couponAllocation.primaryDiscount;
    } else if (isEvent) {
      // The API rounds with Math.round; toFixed from accounting-js adds 1e-8
      // first, which lifts a half cent stored as 10.4999... a cent above the charge.
      return Math.round(
        getCouponDiscountAmount({
          amount: oneTimeSubtotalAmount,
          ticketFeesAmount,
          coupon,
          pageId: page._id,
          pageTicketTypes: pageTicketTypeIds,
          purchase: couponPurchase,
        }),
      );
    } else if (!isSubscription) {
      // A fixed amount bigger than its base stops there, so the setup fee stays owed unless
      // the coupon covers it (get-checkout-charge-data).
      return Math.min(
        Number(toFixed(orderCouponDiscountAmount, 0)),
        Math.max(0, oneTimeSubtotalAmount + (coupon?.appliesToSetupFee ? setupFeeAmount : 0)),
      );
    } else {
      discount = orderCouponDiscountAmount;
    }

    return Number(toFixed(discount, 0));
  })();

  // Shown at what a one-time coupon took, as the charge stores it (a subscription's leftover
  // reaches recurring bumps). Display only: capping the money figure leaves part-cent residue.
  const oneTimeCouponBase = Math.max(
    0,
    oneTimeSubtotalAmount + setupFeeAmount + (couponReachesTicketFees ? ticketFeesAmount : 0),
  );
  const appliedCouponDiscountAmount =
    (isSubscription
      ? couponDiscountAmount
      : Math.min(couponDiscountAmount, Number(toFixed(oneTimeCouponBase, 0)))) +
    bumpCouponDiscountAmount;

  // For fixed tax mode, calculate the preview tax locally from the hydrated rate rather than
  // calling the Stripe Tax API. This overrides the zero values returned by useTaxCalculation.
  const taxMode = page.tax?.mode || 'stripe';
  const fixedTaxRate =
    taxEnabled && taxMode === 'fixed' ? getFixedTaxRate({ page, product }) : null;

  let recurringFeesAmountForTaxBase = 0;
  let oneTimeFeesAmountForTaxBase = 0;
  // The charge branch's fee list survives the sum so the fixed-mode per-line
  // math below can fold each fee across lines the way the server does.
  let oneTimeFeesForTaxLines = [];
  if (isSubscription) {
    const recurringFeeBase = Math.max(
      0,
      singleIterationAmount + recurringOrderBumpsNetAmount - recurringDiscountAmount,
    );
    const recurringFeesForTaxBase =
      getFees({
        fees: page.fees,
        amount: recurringFeeBase,
        pageType: page.type,
        quantity,
        paymentMethod: selectedPaymentMethod,
      }) || [];
    recurringFeesAmountForTaxBase = recurringFeesForTaxBase.reduce((acc, f) => acc + f.amount, 0);

    const oneTimeFeeBase = Math.max(
      0,
      oneTimeSubtotalAmount +
        oneTimeOrderBumpsNetAmount +
        setupFeeAmount -
        (oneTimeVariantsDiscountAmount + setupFeeDiscountAmount),
    );
    const oneTimeFeesForTaxBase =
      getFees({
        fees: getOneTimeItemFees(page.fees),
        amount: oneTimeFeeBase,
        pageType: page.type,
        quantity,
        paymentMethod: selectedPaymentMethod,
      }) || [];
    oneTimeFeesAmountForTaxBase = oneTimeFeesForTaxBase.reduce((acc, f) => acc + f.amount, 0);
  } else {
    // Bumps join after the coupon floor: the coupon caps against the primary
    // alone, so an over-large discount must not eat into the bump (server rule).
    const oneTimeFeeBase =
      addTicketFeesAfterCoupon(oneTimeSubtotalAmount + setupFeeAmount - couponDiscountAmount) +
      oneTimeOrderBumpsNetAmount;
    oneTimeFeesForTaxLines =
      getFees({
        fees: page.fees,
        amount: oneTimeFeeBase,
        pageType: page.type,
        numberOfTickets: totalPaidTicketQuantity,
        quantity,
        paymentMethod: selectedPaymentMethod,
      }) || [];
    oneTimeFeesAmountForTaxBase = oneTimeFeesForTaxLines.reduce((acc, f) => acc + f.amount, 0);
  }

  if (fixedTaxRate) {
    const rate = fixedTaxRate.percentage;
    const isInclusive = fixedTaxRate.inclusive;

    if (isSubscription) {
      const recurringBase = Math.max(
        0,
        singleIterationAmount +
          recurringOrderBumpsNetAmount -
          recurringDiscountAmount +
          recurringFeesAmountForTaxBase,
      );
      if (isInclusive) {
        recurringTaxAmount = Math.round((recurringBase * rate) / (100 + rate));
        recurringAmountExcludingTax = recurringBase - recurringTaxAmount;
      } else {
        recurringTaxAmount = Math.round((recurringBase * rate) / 100);
        recurringAmountExcludingTax = recurringBase;
      }
    }

    if (!isSubscription && oneTimeSelectedOrderBumps.length > 0) {
      // Per-line mirror of the server's buildChargeTaxLines +
      // calculateFixedTaxForLines (task 11): each line taxed by its own
      // product's rate, page fees folded across lines pro-rata by discounted
      // share (largest remainder). The subscription branch stays aggregate on
      // purpose — the server's subscription fixed mode applies the page-level
      // rates to the whole invoice, bumps included.
      // An event's booking fees belong to its tickets line, as on the server;
      // leaving them out showed less than the charge took.
      const perLine = calculateFixedTaxForOrderBumpLines({
        primaryAmount: addTicketFeesAfterCoupon(
          oneTimeSubtotalAmount + setupFeeAmount - couponDiscountAmount,
        ),
        primaryUndiscountedAmount: oneTimeSubtotalAmount + setupFeeAmount + ticketFeesAmount,
        primaryTitle: product.title,
        primaryTaxRate: fixedTaxRate,
        oneTimeSelectedOrderBumps,
        netOrderBumpLineAmount,
        fees: oneTimeFeesForTaxLines,
      });

      oneTimeTaxAmount = perLine.oneTimeTaxAmount;
      oneTimeTotalAmountExcludingTax = perLine.oneTimeTotalAmountExcludingTax;
      oneTimeTotalIncludingTax = perLine.oneTimeTotalIncludingTax;
      taxBreakdownByLine = perLine.taxBreakdownByLine;
    } else {
      // Bake setup fee + fees into the tax base so the local preview matches the server's
      // calculateFixedTax (which receives subtotal + setupFee + fees − coupon). Subscriptions
      // restrict the fee portion to percentage fees and use variant/setup-fee discounts.
      const oneTimeBase = isSubscription
        ? Math.max(
            0,
            oneTimeSubtotalAmount +
              oneTimeOrderBumpsNetAmount +
              oneTimeFeesAmountForTaxBase +
              setupFeeAmount -
              (oneTimeVariantsDiscountAmount + setupFeeDiscountAmount),
          )
        : addTicketFeesAfterCoupon(oneTimeSubtotalAmount + setupFeeAmount - couponDiscountAmount) +
          oneTimeOrderBumpsNetAmount +
          oneTimeFeesAmountForTaxBase;

      if (isInclusive) {
        oneTimeTaxAmount = Math.round((oneTimeBase * rate) / (100 + rate));
        oneTimeTotalAmountExcludingTax = oneTimeBase - oneTimeTaxAmount;
        oneTimeTotalIncludingTax = oneTimeBase;
      } else {
        oneTimeTaxAmount = Math.round((oneTimeBase * rate) / 100);
        oneTimeTotalAmountExcludingTax = oneTimeBase;
        oneTimeTotalIncludingTax = oneTimeBase + oneTimeTaxAmount;
      }
    }
  }

  const hasTax =
    taxEnabled &&
    (recurringAmountExcludingTax > 0 ||
      oneTimeTotalAmountExcludingTax > 0 ||
      oneTimeTotalIncludingTax > 0);

  let recurringTotalAmountBeforeProration = 0;
  let recurringTotalAmount = 0;
  if (isSubscription) {
    recurringTotalAmountBeforeProration = getRecurringTotalAmount({
      recurringAmountExcludingTax: recurringAmountExcludingTaxBeforeProration,
      recurringTaxAmount: recurringTaxAmountBeforeProration,
      recurringSubtotalAmount: singleIterationAmount + recurringOrderBumpsNetAmount,
    });

    recurringTotalAmount = getRecurringTotalAmount({
      recurringAmountExcludingTax,
      recurringTaxAmount,
      recurringSubtotalAmount: singleIterationAmount + recurringOrderBumpsNetAmount,
    });
  }

  const oneTimeTotalAmount = getOneTimeTotalAmount({
    productType: effectiveProductType,
    oneTimeTaxAmount,
    oneTimeTotalAmountExcludingTax,
    oneTimeSubtotalAmount,
    oneTimeVariantsAmount,
    setupFeeAmount,
    hasTax,
  });

  let totalDiscountAmount = 0;
  if (isSubscription) {
    totalDiscountAmount = appliedCouponDiscountAmount;
  } else {
    totalDiscountAmount = totalBulkDiscountAmount + appliedCouponDiscountAmount;
  }

  // Stripe Elements can't switch an intent's mode via options, so a mode change
  // remounts <Elements> (see StripeElementsProvider's key). Switching price-picker
  // tabs transiently clears the selection; recomputing the mode from scratch would
  // flip it on a mixed-billing product (one-time 'payment' <-> recurring
  // 'subscription') and remount the whole checkout, snapping the picker's active
  // tab back so a different-billing tab can never be opened. Hold the last selected
  // price's mode across a clear so the tab switch isn't blocked; the mode still
  // updates the moment a new price is chosen. CartCostProvider sits ABOVE
  // StripeElementsProvider, so this ref survives the Elements remount.
  const computedMode = getMode({ page, product, selectedPrice });
  const lastSelectedModeRef = React.useRef(computedMode);
  if (selectedPrice) lastSelectedModeRef.current = computedMode;
  const mode = selectedPrice ? computedMode : lastSelectedModeRef.current;

  // get list of fees that are based on the one time amount and recurring amount (single iteration)
  let fees;
  let totalAmount;
  let totalDueAmountBeforeProration;
  let totalDueAmount;

  if (isSubscription && hasTax) {
    // Subscription with Stripe Tax

    // getRecurringTotalAmount falls through to recurringSubtotalAmount when
    // recurringAmountExcludingTax === 0, which happens both when tax is loading and
    // when a coupon zeroes the recurring base. In the latter case the fall-through
    // re-adds the full subtotal — bypass it by trusting the tax-calculated values.
    recurringTotalAmount = recurringAmountExcludingTax + recurringTaxAmount;
    recurringTotalAmountBeforeProration =
      recurringAmountExcludingTaxBeforeProration + recurringTaxAmountBeforeProration;

    const recurringTotalAmountBeforeTax = getRecurringTotalAmount({
      recurringSubtotalAmount: singleIterationAmount,
      totalDiscountAmount: recurringDiscountAmount,
    });

    const oneTimeTotalAmountBeforeTax = getOneTimeTotalAmount({
      productType: effectiveProductType,
      oneTimeSubtotalAmount,
      oneTimeVariantsAmount,
      setupFeeAmount,
    });

    fees = getFees({
      fees: page.fees,
      amount:
        recurringTotalAmountBeforeTax +
        oneTimeTotalAmountBeforeTax +
        orderBumpsNetAmount -
        couponDiscountAmount,
      pageType: page.type,
      quantity,
      paymentMethod: selectedPaymentMethod,
    });

    if (hasProration) {
      totalDueAmountBeforeProration = getTotalDueAmount({
        productType: effectiveProductType,
        oneTimeTotalAmount,
        recurringTotalAmount: recurringTotalAmountBeforeProration,
        startDate: effectiveStartDate,
        trialPeriodDays: effectiveTrialPeriodDays,
      });

      totalDueAmount = getTotalDueAmount({
        productType: effectiveProductType,
        oneTimeTotalAmount,
        recurringTotalAmount,
        startDate: effectiveStartDate,
        trialPeriodDays: effectiveTrialPeriodDays,
      });
    } else {
      totalDueAmount = getTotalDueAmount({
        productType: effectiveProductType,
        oneTimeTotalAmount,
        recurringTotalAmount,
        startDate: effectiveStartDate,
        trialPeriodDays: effectiveTrialPeriodDays,
      });
    }

    totalAmount = getTotalDueAmount({
      productType: effectiveProductType,
      oneTimeTotalAmount,
      recurringTotalAmount,
    });
  } else if (isSubscription) {
    // Subscription without Stripe Tax
    let totalAmount = Math.max(
      0,
      oneTimeTotalAmount + oneTimeOrderBumpsNetAmount + recurringTotalAmount - couponDiscountAmount,
    );

    const oneTimeFees = getFees({
      fees: getOneTimeItemFees(page.fees),
      amount:
        oneTimeTotalAmount +
        oneTimeOrderBumpsNetAmount -
        (oneTimeVariantsDiscountAmount + setupFeeDiscountAmount),
      pageType: page.type,
      numberOfTickets: totalPaidTicketQuantity,
      quantity,
      paymentMethod: selectedPaymentMethod,
    });

    fees = getFees({
      fees: page.fees,
      amount: totalAmount,
      pageType: page.type,
      numberOfTickets: totalPaidTicketQuantity,
      quantity,
      paymentMethod: selectedPaymentMethod,
    });

    let oneTimeDiscountAmount;
    if (isSubscription) {
      oneTimeDiscountAmount = oneTimeVariantsDiscountAmount + setupFeeDiscountAmount;
    } else {
      oneTimeDiscountAmount = couponDiscountAmount;
    }

    if (hasProration) {
      totalDueAmountBeforeProration = getTotalDueAmount({
        productType: effectiveProductType,
        oneTimeTotalAmount:
          Math.max(0, oneTimeTotalAmount - oneTimeDiscountAmount) + oneTimeOrderBumpsNetAmount,
        recurringTotalAmount: Math.max(0, recurringTotalAmount - recurringDiscountAmount),
        oneTimeFees,
        fees,
        startDate: effectiveStartDate,
        trialPeriodDays: effectiveTrialPeriodDays,
      });

      totalDueAmount = getTotalDueAmount({
        productType: effectiveProductType,
        oneTimeTotalAmount:
          Math.max(0, oneTimeTotalAmount - oneTimeDiscountAmount) + oneTimeOrderBumpsNetAmount,
        recurringTotalAmount: Math.max(0, prorationAmount), // recurring discount is already included in the proration amount
        oneTimeFees,
        // The proration preview carries the recurring fee; the one-time money's fee items are billed on top.
        fees: oneTimeFees,
        startDate: effectiveStartDate,
        trialPeriodDays: effectiveTrialPeriodDays,
      });
    } else {
      totalDueAmount = getTotalDueAmount({
        productType: effectiveProductType,
        oneTimeTotalAmount:
          Math.max(0, oneTimeTotalAmount - oneTimeDiscountAmount) + oneTimeOrderBumpsNetAmount,
        recurringTotalAmount: Math.max(0, recurringTotalAmount - recurringDiscountAmount),
        oneTimeFees,
        fees,
        startDate: effectiveStartDate,
        trialPeriodDays: effectiveTrialPeriodDays,
      });
    }

    // When an amountOff coupon exceeds the discountable subtotal, Stripe applies the
    // leftover to the fee line items (via the subscription-level discount capping at
    // invoice subtotal). Mirror that here so fixed fees are zeroed by big amountOffs —
    // matches the backend's getSubscriptionItems + getStripeSubscriptionData flow.
    // percentOff is already handled correctly upstream: the fee base is post-coupon
    // (% × max(0, sub − recurringDiscount)), and percentOff is attached only to
    // subscription/variant items on Stripe's side, not to fee items.
    if (coupon && coupon.amountOff && fees && couponReachesSubscription && !isAmountOffOnMainItem) {
      const totalFeeAmount = fees.reduce((sum, f) => sum + f.amount, 0);
      if (totalFeeAmount > 0) {
        const discountableSubtotal =
          recurringSubtotalAmount +
          oneTimeSubtotalAmount +
          (coupon.appliesToSetupFee ? setupFeeAmount : 0) +
          (isAmountOffSplitWithBumps
            ? reachedBumpsBase('recurring') + reachedBumpsBase('one_time')
            : 0);
        const remainingCoupon = Math.max(0, coupon.amountOff - discountableSubtotal);
        const feeDiscountAmount = Math.min(remainingCoupon, totalFeeAmount);
        if (feeDiscountAmount > 0) {
          if (totalDueAmountBeforeProration != null) {
            totalDueAmountBeforeProration = Math.max(
              0,
              totalDueAmountBeforeProration - feeDiscountAmount,
            );
          }
          totalDueAmount = Math.max(0, totalDueAmount - feeDiscountAmount);
        }
      }
    }

    // not entirely sure if this is accurate for subscriptions
    totalAmount = getTotalDueAmount({
      productType: effectiveProductType,
      oneTimeTotalAmount:
        Math.max(0, oneTimeTotalAmount - oneTimeDiscountAmount) + oneTimeOrderBumpsNetAmount,
      recurringTotalAmount: Math.max(0, recurringTotalAmount - recurringDiscountAmount),
      fees,
    });

    // remove the recurring discount from the recurring total amount
    recurringTotalAmount = Math.max(0, recurringTotalAmount - recurringDiscountAmount);
  } else if ((isCharge || isEvent) && hasTax) {
    // One-time payment with Stripe Tax

    // get the one time total amount before tax to calculate the fees to display
    // prevents the fees from being recalculated with tax
    const oneTimeTotalAmountBeforeTax = getOneTimeTotalAmount({
      productType: effectiveProductType,
      oneTimeSubtotalAmount,
      setupFeeAmount,
    });

    // calculate the fees to display
    fees = getFees({
      fees: page.fees,
      amount:
        addTicketFeesAfterCoupon(oneTimeTotalAmountBeforeTax - couponDiscountAmount) +
        oneTimeOrderBumpsNetAmount,
      numberOfTickets: totalPaidTicketQuantity,

      pageType: page.type,
      quantity,
      paymentMethod: selectedPaymentMethod,
    });

    // oneTimeTotalIncludingTax already includes subtotal + setupFee + fees + tax for both
    // Stripe Tax (via the API) and fixed tax (via the oneTimeBase above), so we don't
    // re-add fees or setupFee here.
    totalDueAmount = getTotalDueAmount({
      productType: 'charge',
      oneTimeTotalAmount: Math.max(0, oneTimeTotalIncludingTax),
      fees: [],
    });

    totalAmount = getTotalDueAmount({
      productType: effectiveProductType,
      oneTimeTotalAmount:
        Math.max(0, oneTimeTotalAmount) + ticketFeesAmount + oneTimeOrderBumpsAmount,
      fees,
    });
  } else if (isCharge || isEvent) {
    // One-time payment
    // Bumps join after the coupon floor (the coupon caps against the primary
    // alone, server rule) but before fees, so percentage fees see them.
    let totalAmount =
      addTicketFeesAfterCoupon(oneTimeTotalAmount - couponDiscountAmount) +
      oneTimeOrderBumpsNetAmount; // bulk discount amount has already been deducted from oneTimeTotalAmount

    fees = getFees({
      fees: page.fees,
      amount: totalAmount,
      pageType: page.type,
      numberOfTickets: totalPaidTicketQuantity,
      quantity,
      paymentMethod: selectedPaymentMethod,
    });

    totalDueAmount = getTotalDueAmount({
      productType: effectiveProductType,
      oneTimeTotalAmount:
        addTicketFeesAfterCoupon(oneTimeTotalAmount - couponDiscountAmount) +
        oneTimeOrderBumpsNetAmount,
      fees,
    });

    totalAmount = getTotalDueAmount({
      productType: effectiveProductType,
      oneTimeTotalAmount:
        Math.max(0, oneTimeTotalAmount) + ticketFeesAmount + oneTimeOrderBumpsAmount,
      fees,
    });
  }

  // After calculating the initial totalDueAmount, add this code:
  if (paymentOption.type === 'partial') {
    const partialAmount = getPartialAmount(totalDueAmount, paymentOption.partialAmount);
    totalDueAmount = partialAmount;
  }

  // Kept out of `fees`, which feed the tax bases. A free trial with nothing due now still offers
  // the box, since every renewal pays one too.
  const canCoverProcessingFees = checkCanCoverProcessingFees({ page, paymentOption });
  const coverProcessingFees = getCoverProcessingFeesSetting(page);
  const dueNowProcessingFeeOffer = canCoverProcessingFees
    ? getProcessingFeeContribution(coverProcessingFees, totalDueAmount)
    : null;
  // A taxed recurring total comes from the tax preview, which already holds the fee lines.
  const renewalProcessingFeeBase =
    recurringTotalAmount + (hasTax ? 0 : recurringFeesAmountForTaxBase);
  const recurringProcessingFeeOffer =
    canCoverProcessingFees && isSubscription
      ? getProcessingFeeContribution(coverProcessingFees, renewalProcessingFeeBase)
      : null;
  const processingFeeContributionOffer = dueNowProcessingFeeOffer ?? recurringProcessingFeeOffer;
  const isProcessingFeeTicked = getIsCoverProcessingFeeTicked({ page, form });
  const processingFeeContribution = isProcessingFeeTicked ? dueNowProcessingFeeOffer : null;
  const recurringProcessingFeeContribution = isProcessingFeeTicked
    ? recurringProcessingFeeOffer
    : null;
  if (processingFeeContribution) {
    totalDueAmount += processingFeeContribution.amount;
  }

  const orderBumpLineItems = selectedOrderBumps.map(selection => ({
    id: String(selection.offer.productId),
    type: 'order_bump',
    name: selection.offer.title,
    quantity: selection.quantity,
    unitAmount: orderBumpUnitPrice(selection),
    amount: orderBumpUnitPrice(selection) * selection.quantity,
    // The fee stays off `amount` (the Order splits it into its own line) and
    // gets its own breakdown row instead.
    ...(selection.offer.price.setupFee > 0 && {
      setupFeeAmount:
        orderBumpLineAmount(selection) - orderBumpUnitPrice(selection) * selection.quantity,
      setupFeeQuantity: selection.offer.price.setupFeeMultipliesWithQuantity
        ? selection.quantity
        : 1,
    }),
    ...(bumpCouponDiscountBySelection.get(selection) > 0 && {
      couponDiscountAmount: bumpCouponDiscountBySelection.get(selection),
    }),
    billingType: selection.offer.billingType,
    ...(selection.offer.billingType === 'recurring' && {
      interval: selection.offer.interval,
      intervalCount: selection.offer.intervalCount ?? 1,
    }),
  }));

  // The API's per-line rows carry only a reference — resolve the display title
  // here so PriceBreakdown doesn't need the offer list. The fixed-mode local
  // rows already carry one.
  const taxLineTitleByReference = new Map([
    ['primary', product.title],
    ...selectedOrderBumps.map(({ offer }) => [`bump:${offer.productId}`, offer.title]),
  ]);
  const resolvedTaxBreakdownByLine =
    selectedOrderBumps.length > 0
      ? taxBreakdownByLine?.map(row => ({
          ...row,
          title: row.title ?? taxLineTitleByReference.get(row.reference),
        }))
      : undefined;

  const primaryOneTimeVariants = isSubscription
    ? selectedVariants
        .map(variant => ({
          name: variant.name,
          amount: getOneTimeVariantsAmount([variant], quantity, 'subscription')
            .oneTimeVariantsAmount,
        }))
        .filter(line => line.amount > 0)
    : [];

  const cartCost = {
    currency,
    mode,

    lineItems: [...ticketTypeLineItems, ...orderBumpLineItems],

    // tickets
    selectedTicketTypes,
    totalTicketQuantity,
    totalPaidTicketQuantity,
    // The ticket-fee pool as charged: per-ticket fee x quantity, less a
    // ticket-scoped coupon's share. NOT inside subtotalAmount.
    ticketFeesAmount,
    // The same pool before that share, which the breakdown's subtotal shows.
    ticketFeesSubtotalAmount: isEvent ? totalTicketFeesSubtotalAmount : 0,

    // product
    productPriceAmount, // The (dynamic) price of the main product, excluding variants
    planIterations, // The (dynamic) number of iterations for the product

    // product, variants & quantity
    quantity,
    // The primary's own subtotal, so the breakdown can draw a main product
    // row without backing bump money (setup fees included) out of subtotalAmount.
    primarySubtotalAmount: subtotalAmount,
    ...(primaryOneTimeVariants.length && { primaryOneTimeVariants }),
    subtotalAmount: subtotalAmount + orderBumpsAmount, // The subtotal, excluding the primary's setup fee (a bump carries its own), excluding discounts, before taxes, shipping, and other adjustments
    oneTimeSubtotalAmount: oneTimeSubtotalAmount + oneTimeOrderBumpsAmount, // The subtotal for one-time items, excluding the primary's setup fee (a bump carries its own), excluding discounts, before taxes, shipping, and other adjustments
    recurringSubtotalAmount: recurringSubtotalAmount + recurringOrderBumpsAmount, // The subtotal for recurring items, excluding the primary's setup fee (a bump carries its own), excluding discounts, before taxes, shipping, and other adjustments

    shippingAmount: 0, // The shipping amount

    // discounts
    subtotalBulkDiscount,
    totalBulkDiscountAmount,
    coupon,
    couponDiscountAmount: appliedCouponDiscountAmount,
    // False while the cart holds none of what the coupon picks; a free trial still reaches the plan.
    couponReachesCart: Boolean(coupon) && couponAllocation.reachesOrder,
    totalDiscountAmount: totalDiscountAmount,
    // setup fee
    setupFeeAmount, // The setup fee

    // recurring tax
    recurringTaxAmount,
    recurringAmountExcludingTax,
    recurringTaxAmounts,

    // recurring tax before proration
    recurringTaxAmountBeforeProration,
    recurringAmountExcludingTaxBeforeProration,
    recurringTaxAmountsBeforeProration,

    // one-time tax
    oneTimeTaxAmount,
    oneTimeTotalAmountExcludingTax,
    oneTimeTaxAmounts,

    // total tax (combined)
    totalTaxAmounts,
    totalTaxAmountsBeforeProration,

    isCalculatingTax,
    isLoadingProration,
    isRefetchingProration,
    hasProration,
    isLoading: isCalculatingTax || isRefetchingProration,

    // fees
    fees,

    // total amount including all fees and taxes, before discounts
    totalAmount,

    // totals
    // The subscription+tax figure is already bump-inclusive — the tax
    // preview's one-time invoice carries the bump items (stripe mode) and the
    // fixed-mode base above adds them — so adding here again would
    // double-count. Every other branch's local figure is primary-only.
    // (Condition mirrors getOneTimeTotalAmount's own branch.)
    oneTimeTotalAmount:
      effectiveProductType === 'subscription' && hasTax
        ? oneTimeTotalAmount
        : oneTimeTotalAmount + oneTimeOrderBumpsAmount, // The total one-time amount, not including setup fee
    recurringTotalAmount, // The total recurring amount, not including setup fee
    totalDueAmountBeforeProration,
    totalDueAmount, // The total amount due now after all discounts, taxes, shipping, and other adjustments
    processingFeeContribution,
    processingFeeContributionOffer,
    recurringProcessingFeeContribution,

    // Per-line tax rows ({ reference, title, taxAmount, rateLabel }) — only
    // present on an order-bump purchase, so a bump-free cart keeps today's
    // exact shape.
    ...(resolvedTaxBreakdownByLine && { taxBreakdownByLine: resolvedTaxBreakdownByLine }),
  };

  // only attach to window on client
  if (!isProduction && typeof window !== 'undefined') {
    window.cart = cartCost;
  }

  const ticketCouponContext = { shares: ticketCouponShares, purchase: couponPurchase };

  return (
    <CartCostContext.Provider value={cartCost}>
      <TicketCouponSharesContext.Provider value={ticketCouponContext}>
        {children}
      </TicketCouponSharesContext.Provider>
    </CartCostContext.Provider>
  );
}

export default function useCartCost() {
  const { page } = useCheckoutState();

  if (page.type === PAGE_TYPES.FORM) return {};

  const context = React.useContext(CartCostContext);

  if (context === undefined) {
    throw new Error('useCartCost must be used within a CartCostProvider');
  }

  return context;
}

/** A ticket type's share of an amount off it shares with add-ons; 0 outside a cart. */
export function useTicketCouponShare(ticketTypeId) {
  return React.useContext(TicketCouponSharesContext)?.shares?.[ticketTypeId] ?? 0;
}

/** The tickets and bumps in the cart, which the coupon's rules match on; empty outside a cart. */
export function useCouponPurchase() {
  return React.useContext(TicketCouponSharesContext)?.purchase ?? {};
}

export { CartCostProvider };
